// Secret-value detection for the Buyer integration screen (CU-LS-M1-050).
// Rule: the screen never accepts a secret VALUE anywhere. A credential must live in an env var on the
// API and the template may only carry its NAME (LDS_BUYER_SECRET_*) in `secretRef`.
// Messages never echo the offending value.

// Buyer field / header names that carry a credential ("marked sensitive"): a typed value is refused.
const CREDENTIAL_NAME =
  /(pass(word|wd|phrase|code)|pwd|secret|token|api[_-]?key|access[_-]?key|private[_-]?key|credential|authori[sz]ation|authenticat(e|ion|or)|signature|session[_-]?id|cookie)/i;
// Short credential words as a whole name or a separated part: "auth", "x-auth", "pass", "user_pw".
const SHORT_CREDENTIAL_NAME = /(^|[_.-])(auth|pass|pw|pin|sig|otp)([_.-]|$)/i;
// "source_key", "partner.key", "sourceKey", "key": buyer keys are credentials too.
const KEY_NAME = /(^|[_.-])key$/i;
const CAMEL_KEY_NAME = /[a-z0-9]Key$/;
// Account ids that pair with a password (Phonexa apiId, OAuth client_id) are credentials as well.
const ACCOUNT_ID_NAME = /^(api|client|account|app)[_.-]?(id|user|login)$/i;

export function isCredentialName(name) {
  if (typeof name !== "string") return false;
  // every component of a nested / bracketed name ("applicant.api_id", "data[apiKey]")
  const parts = [name, ...name.split(/[.[\]]+/).filter(Boolean)];
  return parts.some(
    (part) =>
      CREDENTIAL_NAME.test(part) ||
      SHORT_CREDENTIAL_NAME.test(part.replace(/([a-z0-9])([A-Z])/g, "$1_$2")) ||
      KEY_NAME.test(part) ||
      CAMEL_KEY_NAME.test(part) ||
      ACCOUNT_ID_NAME.test(part)
  );
}

// Well-known credential shapes. Checked on every string the editors hold (except secretRef names).
const KNOWN_SECRET_PATTERNS = [
  ["private key", /-----BEGIN [A-Z ]*PRIVATE KEY-----/],
  ["auth header value", /^\s*bearer\s+\S+/i],
  ["JWT", /^eyJ[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}\.?[A-Za-z0-9_-]*$/],
  ["Stripe-style key", /^(sk|pk|rk)_(live|test)_[A-Za-z0-9]{8,}$/],
  ["AWS access key", /^(AKIA|ASIA)[A-Z0-9]{16}$/],
  ["GitHub token", /^(gh[pousr]_[A-Za-z0-9]{20,}|github_pat_[A-Za-z0-9_]{20,})$/],
  ["Slack token", /^xox[abprs]-[A-Za-z0-9-]{10,}$/],
  ["Google API key", /^AIza[0-9A-Za-z_-]{30,}$/],
  ["UUID-shaped key", /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i],
  ["long hex string", /^[0-9a-f]{32,}$/i],
  ["long base64 string", /^(?!\/)(?=.*\d)(?=.*[A-Za-z])[A-Za-z0-9+/_-]{32,}={0,2}$/],
  ["credential in URL", /^[a-z][a-z0-9+.-]*:\/\/[^/\s:@]+:[^/\s@]+@/i],
  [
    "credential in URL query",
    /[?&](api[_-]?key|key|token|access[_-]?token|secret|password|pass|pwd|sig|signature|auth)=[^&\s]+/i,
  ],
];

function shannonEntropy(text) {
  const counts = {};
  for (const ch of text) counts[ch] = (counts[ch] || 0) + 1;
  let bits = 0;
  Object.values(counts).forEach((n) => {
    const p = n / text.length;
    bits -= p * Math.log2(p);
  });
  return bits;
}

function charClasses(text) {
  return [/[a-z]/, /[A-Z]/, /[0-9]/, /[^A-Za-z0-9]/].filter((re) => re.test(text)).length;
}

// High-entropy, random-looking token: no spaces, not a URL. Letters+digits mixes need less entropy than
// letters only (random mixed-case keys), which must also switch case often to rule out words.
export function isHighEntropy(text) {
  if (typeof text !== "string") return false;
  const s = text.trim();
  if (s.length < 16 || /\s/.test(s) || s.includes("://")) return false;
  if (!/[A-Za-z]/.test(s)) return false;
  // Structured email constants are ordinary buyer data; credential-named fields are checked separately.
  if (/^[A-Za-z0-9.!#$%&'*+/=?^_`{|}~-]+@[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?(?:\.[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?)+$/.test(s)) return false;
  // dates and ISO timestamps are data, not secrets
  if (/^\d{4}-\d{2}-\d{2}([T ]\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}:?\d{2})?)?$/i.test(s)) return false;
  // identifiers made of word-like parts ("decision_status_code_2024", "TBD-LEAD-TYPE") are not secrets
  // (camelCase and PascalCase humps need 3+ lower-case letters, so random "QwErTy..." is not a word)
  const WORD = /^([a-z]+|[A-Z]+|[0-9]+|[a-z]+([A-Z][a-z]{2,})+|([A-Z][a-z]{2,})+)[0-9]*$/;
  const parts = s.split(/[_./-]+/).filter(Boolean);
  // long single-case runs are words only when they read like words (low entropy)
  const randomRun = (p) => {
    const alpha = p.replace(/[0-9]+$/, "");
    return alpha.length > 15 && /^([a-z]+|[A-Z]+)$/.test(alpha) && shannonEntropy(alpha) >= 3.9;
  };
  if (parts.length && parts.every((p) => WORD.test(p) && !randomRun(p))) return false;
  const entropy = shannonEntropy(s);
  if (/[0-9]/.test(s)) {
    const classes = charClasses(s);
    return (s.length >= 20 && entropy >= 3.5) || (classes >= 3 && entropy >= 3.3);
  }
  if (parts.some(randomRun)) return true;
  const caseSwitches = (s.match(/[a-z][A-Z]|[A-Z][a-z]/g) || []).length;
  return s.length >= 20 && entropy >= 3.8 && /[a-z]/.test(s) && /[A-Z]/.test(s) && caseSwitches >= s.length / 4;
}

// Returns why `text` looks like a secret, or null. Modes:
// - "value" (default for data sent to the buyer): known shapes, credential name=value pairs, high entropy;
// - "structural" (content type, enums): the same, but entropy only on intact tokens;
// - "pattern" (JSON paths, XPath, regexes): known shapes and intact random tokens; name=value pairs only
//   when the value is a literal (no regex syntax), so `token="([^"]+)"` stays a valid rule.
// `valueLike` is kept for callers: true = "value", false = "structural".
const REGEX_SYNTAX = /[()[\]\\*+?{}|^]/;

export function secretReason(text, { valueLike = false, mode } = {}) {
  if (typeof text !== "string" || text.trim() === "") return null;
  const kind = mode || (valueLike ? "value" : "structural");
  const s = text.trim();
  for (const [label, re] of KNOWN_SECRET_PATTERNS) {
    if (re.test(s)) return label;
  }
  // every URL inside the string, whole or embedded ("callback=https://...")
  for (const url of s.match(/[a-z][a-z0-9+.-]*:\/\/[^\s"'<>]+/gi) || []) {
    const reason = urlSecretReason(url);
    if (reason) return reason;
  }
  if (kind === "value" && isHighEntropy(s)) return "high-entropy string";
  // name=value / "name": "value" pairs with a credential name
  const pair = /["']?([A-Za-z0-9_.[\]-]+)["']?\s*[=:]\s*("[^"]*"|'[^']*'|[^\s;,&"'}]+)/g;
  let m;
  while ((m = pair.exec(s)) !== null) {
    const value = m[2].replace(/^["']|["']$/g, "");
    if (!isCredentialName(m[1]) || value.trim() === "") continue;
    const nameStart = m.index + m[0].indexOf(m[1]);
    const before = s[nameStart - 1];
    const prefix = s.slice(0, nameStart);
    const insidePredicate = (prefix.match(/\[/g) || []).length > (prefix.match(/\]/g) || []).length;
    // XPath predicates ([@key='status'], [meta/key="status"]) name nodes, not credentials
    if (kind === "pattern" && (insidePredicate || before === "@" || before === "/")) continue;
    if (kind === "pattern" && REGEX_SYNTAX.test(value)) continue;
    return "credential parameter";
  }
  // XML elements with a credential name: <password>summer</password>
  if (kind !== "pattern") {
    const element = /<([A-Za-z_][\w.:-]*)[^>]*>((?:<!\[CDATA\[[\s\S]*?\]\]>|[^<])*)<\/\1\s*>/g;
    let e;
    while ((e = element.exec(s)) !== null) {
      const content = e[2].replace(/<!\[CDATA\[([\s\S]*?)\]\]>/g, "$1");
      if (isCredentialName(e[1].split(":").pop()) && content.trim() !== "") return "credential parameter";
    }
  }
  // tokens inside a longer string ("ApiKey <token>", "a; ref=<token>", "x/<token>")
  const tokens = s
    .split(/[\s,;:="'&|<>()]+/)
    .map((t) => t.replace(/^[^A-Za-z0-9+/_-]+|[^A-Za-z0-9+/=_-]+$/g, ""))
    .filter(Boolean);
  // "Basic <b64 of user:pass>" / "Bearer <token>" anywhere in the string
  if (/\bbearer\s+\S+/i.test(s)) return "auth header value";
  const basic = /\bbasic\s+([A-Za-z0-9+/]{4,}={0,2})(?![A-Za-z0-9])/gi;
  let b;
  while ((b = basic.exec(s)) !== null) {
    if (isBasicCredential(b[1])) return "auth header value";
  }
  for (const token of tokens) {
    if (token !== s) {
      for (const [label, re] of KNOWN_SECRET_PATTERNS) {
        if (re.test(token)) return label;
      }
      if (token.includes("://") && urlSecretReason(token)) return urlSecretReason(token);
    }
    const intact = /^[A-Za-z0-9_+/-]+=*$/.test(token) && !token.startsWith("/");
    if ((kind === "value" || intact) && token !== s && isHighEntropy(token)) return "high-entropy string";
    if (kind !== "value" && intact && token === s && isHighEntropy(token)) return "high-entropy string";
  }
  // paths and regexes: random-looking runs between punctuation
  if (kind !== "value") {
    const runs = s.split(/[^A-Za-z0-9+_-]+/).filter((r) => r.length >= 16);
    if (runs.some(isHighEntropy)) return "high-entropy string";
  }
  return null;
}

// base64 of "user:password" (printable, with a colon)
function isBasicCredential(b64) {
  try {
    const decoded = atob(b64);
    // any "user:..." (passwords may hold colons or non-ASCII bytes)
    return decoded.indexOf(":") >= 0;
  } catch (e) {
    return false;
  }
}

const safeDecode = (part) => {
  try {
    return decodeURIComponent(part);
  } catch (e) {
    return part;
  }
};

// A URL is checked part by part: user info, every path segment, every query/fragment parameter
// (credential-named parameters with a value, and values that look like secrets).
export function urlSecretReason(text) {
  let url;
  try {
    url = new URL(text.trim());
  } catch (e) {
    return null;
  }
  if (url.username || url.password) return "credential in URL";
  const part = (value) => {
    const decoded = safeDecode(value);
    if (decoded.includes("://")) return urlSecretReason(decoded);
    return secretReason(decoded, { valueLike: true });
  };
  for (const label of url.hostname.split(".")) {
    if (label && isHighEntropy(label)) return "secret in URL host";
  }
  for (const segment of url.pathname.split("/")) {
    if (segment && part(segment)) return "secret in URL path";
  }
  const params = [];
  url.searchParams.forEach((value, name) => params.push([name, value]));
  const hash = url.hash.replace(/^#/, "");
  if (hash.includes("=")) {
    new URLSearchParams(hash).forEach((value, name) => params.push([name, value]));
  } else if (hash && part(hash)) {
    return "secret in URL fragment";
  }
  for (const [name, value] of params) {
    if (secretReason(safeDecode(name), { valueLike: true })) return "secret in URL query";
    if (value.trim() !== "" && (isCredentialName(name) || /^(sig|pass|pwd|auth|code)$/i.test(name))) {
      return "credential in URL query";
    }
    if (part(value)) return "secret in URL query";
  }
  return null;
}

const STRUCTURAL_KEYS = new Set(["secretRef", "envRef"]);
// Paths, regexes, media types and enums legitimately look random: only known key shapes are checked there.
const PATTERN_KEYS = new Set(["statusPath", "pricePath", "bestBidPath", "redirectUrlPath", "reasonPath"]);
const STRUCTURAL_VALUE_KEYS = new Set([
  "contentType",
  "transforms",
  "format",
  "outcome",
  "type",
  "ldsField",
]);

// Walks any JSON value and reports secret-looking strings by path (never by value).
export function findSecretValues(root, rootName) {
  const found = [];
  const walk = (node, path, key) => {
    if (typeof node === "string") {
      if (STRUCTURAL_KEYS.has(key)) return;
      const mode = PATTERN_KEYS.has(key)
        ? "pattern"
        : STRUCTURAL_VALUE_KEYS.has(key)
        ? "structural"
        : "value";
      const reason = secretReason(node, { mode });
      if (reason) found.push({ path, reason });
    } else if (Array.isArray(node)) {
      node.forEach((child, i) => walk(child, `${path}[${i}]`, key));
    } else if (node !== null && typeof node === "object") {
      Object.keys(node).forEach((k) => walk(node[k], path ? `${path}.${k}` : k, k));
    }
  };
  walk(root, rootName, null);
  return found;
}
